import { FencyProvider } from "@fencyai/react";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { fencyPromise } from "../fency/fencySetup";
import { useFency } from "../fency/useFency";
import { useSub } from "../subscription/useSub";
import type { AiResultHandoff } from "./aiResultHandoff";
import type { DocumentScope } from "./documentScope";
import { assistantIntroFor } from "./assistantIntro";
import { AssistantPanel } from "./AssistantPanel";
import { AssistantPill } from "./AssistantPill";
import { AssistantPanelContext, type AskedQuestion } from "./assistantPanelContext";
import { useAssistant } from "./useAssistant";
import { panelOpenPreference } from "./panelPreferences";
import { usePanelPlacement } from "./usePanelPlacement";
import { PanelLayoutContext } from "../layout/panelLayoutContext";

type AssistantContextValue = ReturnType<typeof useAssistant> & {
  draft: string;
  setDraft: (value: string) => void;
};

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

export const useAssistantContext = (): AssistantContextValue => {
  const context = useContext(AssistantContext);
  if (context === undefined) {
    throw new Error("useAssistantContext must be used within an AssistantProvider");
  }
  return context;
};

export const useOptionalAssistantContext = (): AssistantContextValue | undefined => useContext(AssistantContext);

const ActiveAssistantProvider = ({ children }: { children: ReactNode }) => {
  const { fetchCreateStreamClientToken } = useFency();

  return (
    <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
      <AssistantStateProvider>{children}</AssistantStateProvider>
    </FencyProvider>
  );
};

const AssistantStateProvider = ({ children }: { children: ReactNode }) => {
  const assistant = useAssistant();
  const [draft, setDraft] = useState("");
  const contextValue = useMemo(() => ({ ...assistant, draft, setDraft }), [assistant, draft]);
  const { placement, widthSwitchApplies } = usePanelPlacement();
  const layout = useMemo(() => ({ placement }), [placement]);

  return (
    <AssistantContext.Provider value={contextValue}>
      <PanelLayoutContext.Provider value={layout}>
        {children}
        <AssistantPanel placement={placement} widthSwitchApplies={widthSwitchApplies} />
        <AssistantPill />
      </PanelLayoutContext.Provider>
    </AssistantContext.Provider>
  );
};

export const AssistantProvider = ({ children }: { children: ReactNode }) => {
  const { canUseAssistant, canChatWithDocuments } = useSub();
  const personId = useLoggedInWithOrgContextState().me.id;
  const [openPreference, setOpenPreference] = panelOpenPreference.use(personId);
  const setPanelOpen = useCallback(
    (open: boolean) => setOpenPreference(open ? "open" : "closed"),
    [setOpenPreference],
  );
  const [introducing, setIntroducing] = useState(false);
  const [handoff, setHandoff] = useState<AiResultHandoff | null>(null);
  const [documentScope, setDocumentScope] = useState<DocumentScope | null>(null);
  const panelOpen = openPreference === "open" && (canUseAssistant || documentScope !== null);
  const [pageRequest, setPageRequest] = useState(0);
  const [question, setQuestion] = useState<AskedQuestion | null>(null);
  const undelivered = useRef<AiResultHandoff | null>(null);
  const clearHandoff = useCallback(() => {
    undelivered.current = null;
    setHandoff(null);
  }, []);
  const openPanel = useCallback(() => {
    clearHandoff();
    setDocumentScope(null);
    setIntroducing(false);
    setPanelOpen(true);
  }, [clearHandoff, setPanelOpen]);
  const openOnPage = useCallback(() => {
    clearHandoff();
    setDocumentScope(null);
    setIntroducing(false);
    setPageRequest((request) => request + 1);
    setPanelOpen(true);
  }, [clearHandoff, setPanelOpen]);
  const openWithResult = useCallback((result: AiResultHandoff) => {
    setIntroducing(false);
    undelivered.current = result;
    setHandoff(result);
    setDocumentScope(null);
    setPanelOpen(true);
  }, [setPanelOpen]);
  const openWithDocument = useCallback(
    (scope: DocumentScope) => {
      clearHandoff();
      setIntroducing(false);
      setDocumentScope(scope);
      setPanelOpen(true);
    },
    [clearHandoff, setPanelOpen],
  );
  const openWithQuestion = useCallback(
    (text: string, startButtonLabel?: string) => {
      clearHandoff();
      setDocumentScope(null);
      setIntroducing(false);
      setQuestion((previous) => ({ id: (previous?.id ?? 0) + 1, text, startButtonLabel }));
      setPanelOpen(true);
    },
    [clearHandoff, setPanelOpen],
  );
  const takeHandoff = useCallback(() => {
    const result = undelivered.current;
    undelivered.current = null;
    return result;
  }, []);
  const reopenPanel = useCallback(() => {
    setIntroducing(false);
    setPanelOpen(true);
  }, [setPanelOpen]);
  const closePanel = useCallback(() => {
    setIntroducing(false);
    setPanelOpen(false);
  }, [setPanelOpen]);
  const panel = useMemo(
    () => ({
      panelOpen,
      openPanel,
      reopenPanel,
      openOnPage,
      pageRequest,
      closePanel,
      handoff,
      openWithResult,
      takeHandoff,
      clearHandoff,
      documentScope,
      openWithDocument: canChatWithDocuments ? openWithDocument : undefined,
      introducing,
      question,
      openWithQuestion: canUseAssistant ? openWithQuestion : undefined,
    }),
    [
      panelOpen,
      openPanel,
      reopenPanel,
      openOnPage,
      pageRequest,
      closePanel,
      handoff,
      openWithResult,
      takeHandoff,
      clearHandoff,
      documentScope,
      canChatWithDocuments,
      openWithDocument,
      introducing,
      question,
      canUseAssistant,
      openWithQuestion,
    ],
  );

  useEffect(() => {
    const intro = assistantIntroFor(personId);
    if (!canUseAssistant || intro.seen()) return;
    intro.remember();
    setIntroducing(true);
    setPanelOpen(true);
  }, [canUseAssistant, personId, setPanelOpen]);

  return (
    <AssistantPanelContext.Provider value={panel}>
      {canUseAssistant || canChatWithDocuments ? (
        <ActiveAssistantProvider>{children}</ActiveAssistantProvider>
      ) : (
        <>{children}</>
      )}
    </AssistantPanelContext.Provider>
  );
};
